/**
 * Master Attention Orchestrator
 * -----------------------------
 * Single arbiter that decides which emotional surface owns the screen at
 * any instant. Pure in-memory subscriber store — NO polling, NO realtime,
 * NO new engines. All existing surfaces (Big Moments, Viral Capsules,
 * Story Arc, Nation Rivalry, World Status, Predictions, Polls, Global
 * Takeover) plug in by:
 *
 *   • claiming focus with a priority + TTL when they go active, and
 *   • checking `canSurface(key, cadenceMs)` before firing so we get a
 *     real emotional cadence (quiet windows, accumulation, release).
 *
 * Priority hierarchy (visual dominance):
 *
 *   100  global_takeover          (Tier 3 — single dominant focus)
 *    90  big_moment               (goal, red, penalty, last minute)
 *    80  viral_capsule
 *    70  prediction
 *    60  poll
 *    50  story_arc / nation_rivalry / world_status
 *
 * Rule: only ONE focus active at a time. Higher priority preempts lower
 * priority. Secondary surfaces should read `useAttentionYield()` and
 * fade / blur themselves when a higher-priority focus owns the screen.
 */
import { trackEvent } from "@/lib/audit";

export type AttentionKey =
  | "global_takeover"
  | "big_moment"
  | "viral_capsule"
  | "sponsor"
  | "prediction"
  | "poll"
  | "quiniela_cta"
  | "cross_match"
  | "narrative_ticker"
  | "participa_fab"
  | "retention_fab"
  | "story_arc"
  | "nation_rivalry"
  | "world_status";

export const ATTENTION_PRIORITY: Record<AttentionKey, number> = {
  global_takeover: 100,
  big_moment: 90,
  viral_capsule: 80,
  sponsor: 75,
  prediction: 70,
  poll: 60,
  story_arc: 50,
  nation_rivalry: 50,
  world_status: 50,
  quiniela_cta: 40,
  cross_match: 35,
  narrative_ticker: 30,
  participa_fab: 25,
  retention_fab: 20,
};

/** Quiet windows enforced AFTER a key surfaces. Silence economy. */
const CADENCE_MIN_GAP_MS: Record<AttentionKey, number> = {
  global_takeover: 30_000,
  big_moment: 8_000,
  viral_capsule: 12_000,
  sponsor: 0,
  prediction: 9_000,
  poll: 9_000,
  story_arc: 0,
  nation_rivalry: 0,
  world_status: 0,
  quiniela_cta: 0,
  cross_match: 0,
  narrative_ticker: 0,
  participa_fab: 0,
  retention_fab: 0,
};

/** When a high-tier key fires, suppress these others for the given ms. */
const TAKEOVER_SUPPRESSES: Partial<Record<AttentionKey, Partial<Record<AttentionKey, number>>>> = {
  global_takeover: {
    big_moment: 8_000,
    viral_capsule: 12_000,
    sponsor: 12_000,
    prediction: 8_000,
    poll: 8_000,
    narrative_ticker: 8_000,
    cross_match: 8_000,
    quiniela_cta: 8_000,
  },
  big_moment: {
    viral_capsule: 6_000,
    sponsor: 4_000,
    narrative_ticker: 4_000,
  },
  // Sponsor takeover — short TTL suppression of emotional surfaces / ticker / poll / prediction.
  sponsor: {
    prediction: 5_000,
    poll: 5_000,
    narrative_ticker: 5_000,
    story_arc: 5_000,
    nation_rivalry: 5_000,
    world_status: 5_000,
    quiniela_cta: 5_000,
  },
  // Reciprocal exclusion — PredictionCard and LivePollWidget never simultaneously.
  prediction: {
    poll: 8_000,
    narrative_ticker: 4_000,
  },
  poll: {
    prediction: 8_000,
    narrative_ticker: 4_000,
  },
};

interface FocusEntry {
  key: AttentionKey;
  priority: number;
  claimedAt: number;
  expiresAt: number;
  token: number;
}

let currentFocus: FocusEntry | null = null;
const lastSurfaceAt = new Map<AttentionKey, number>();
const suppressUntil = new Map<AttentionKey, number>();
let tokenSeq = 1;

type Listener = () => void;
const listeners = new Set<Listener>();

/**
 * Cached snapshot — MUST be a stable reference while the store has not
 * mutated. `useSyncExternalStore` calls `getSnapshot` after every render
 * and bails out only on referential equality; returning a fresh object
 * here causes React #185 (infinite render loop).
 */
interface AttentionSnapshot {
  key: AttentionKey | null;
  priority: number;
  active: boolean;
}
const EMPTY_SNAPSHOT: AttentionSnapshot = Object.freeze({
  key: null,
  priority: 0,
  active: false,
});
let cachedSnapshot: AttentionSnapshot = EMPTY_SNAPSHOT;

function rebuildSnapshot(): void {
  cachedSnapshot = currentFocus
    ? Object.freeze({
        key: currentFocus.key,
        priority: currentFocus.priority,
        active: true,
      })
    : EMPTY_SNAPSHOT;
}

function emit(): void {
  for (const l of Array.from(listeners)) {
    try { l(); } catch { /* listener isolated */ }
  }
}

function gcExpired(now: number): boolean {
  if (currentFocus && currentFocus.expiresAt <= now) {
    currentFocus = null;
    rebuildSnapshot();
    return true;
  }
  return false;
}

let gcTimer: ReturnType<typeof setTimeout> | null = null;
function scheduleGc(expiresAt: number): void {
  if (typeof setTimeout === "undefined") return;
  if (gcTimer) clearTimeout(gcTimer);
  const delay = Math.max(50, expiresAt - Date.now() + 10);
  gcTimer = setTimeout(() => {
    gcTimer = null;
    if (gcExpired(Date.now())) emit();
  }, delay);
}

export function subscribeAttention(cb: Listener): () => void {
  listeners.add(cb);
  return () => { listeners.delete(cb); };
}

export function getAttentionSnapshot(): AttentionSnapshot {
  // Pure read — no mutation, no allocation. Expiration is handled out
  // of band by `scheduleGc`. Returning a stable reference is required
  // by `useSyncExternalStore` to avoid React #185.
  return cachedSnapshot;
}

/**
 * Claim the dominant focus. Returns a release token. Higher priority
 * preempts a lower-priority active focus. Equal priority refreshes TTL.
 * Returns null when a stronger focus already owns the screen.
 */
export function claimAttention(
  key: AttentionKey,
  ttlMs: number,
  opts?: { priority?: number },
): { release: () => void; token: number } | null {
  const now = Date.now();
  const expiredChanged = gcExpired(now);
  const priority = opts?.priority ?? ATTENTION_PRIORITY[key];
  if (currentFocus && currentFocus.priority > priority) {
    if (expiredChanged) emit();
    return null;
  }
  const token = ++tokenSeq;
  currentFocus = {
    key,
    priority,
    claimedAt: now,
    expiresAt: now + Math.max(500, ttlMs),
    token,
  };
  lastSurfaceAt.set(key, now);
  const sup = TAKEOVER_SUPPRESSES[key];
  if (sup) {
    for (const [k, ms] of Object.entries(sup) as Array<[AttentionKey, number]>) {
      suppressUntil.set(k, Math.max(suppressUntil.get(k) ?? 0, now + ms));
    }
  }
  rebuildSnapshot();
  scheduleGc(currentFocus.expiresAt);
  void trackEvent("attention_focus_claimed", { key, priority, ttl_ms: ttlMs });
  emit();
  return {
    token,
    release: () => releaseAttention(token, key),
  };
}

function releaseAttention(token: number, key: AttentionKey): void {
  if (!currentFocus || currentFocus.token !== token) return;
  currentFocus = null;
  rebuildSnapshot();
  void trackEvent("attention_focus_released", { key });
  emit();
}

/**
 * Should a surface allow itself to render right now?
 *   • Respects cadence min-gap for this key.
 *   • Respects suppression windows from higher-tier surfaces.
 *   • Respects current focus (a higher-priority focus blocks lower).
 */
export function canSurface(key: AttentionKey, extraGapMs?: number): boolean {
  const now = Date.now();
  if (gcExpired(now)) emit();
  const lastAt = lastSurfaceAt.get(key) ?? 0;
  const gap = Math.max(CADENCE_MIN_GAP_MS[key], extraGapMs ?? 0);
  if (gap > 0 && now - lastAt < gap) {
    void trackEvent("attention_cadence_suppressed", { key, reason: "min_gap" });
    return false;
  }
  const supUntil = suppressUntil.get(key) ?? 0;
  if (supUntil > now) {
    void trackEvent("attention_cadence_suppressed", { key, reason: "takeover_quiet_window" });
    return false;
  }
  if (currentFocus && currentFocus.priority > ATTENTION_PRIORITY[key]) {
    void trackEvent("attention_cadence_suppressed", { key, reason: "yields_to_higher" });
    return false;
  }
  return true;
}

/** Mark a surface as having fired without claiming dominant focus. */
export function noteSurface(key: AttentionKey): void {
  lastSurfaceAt.set(key, Date.now());
}

/** Reset — test/SSR helper. */
export function _resetAttention(): void {
  currentFocus = null;
  lastSurfaceAt.clear();
  suppressUntil.clear();
  rebuildSnapshot();
  if (gcTimer) { clearTimeout(gcTimer); gcTimer = null; }
}